import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { BillingApi } from 'app/modules/settings/modules/billing/api/billing';
import { ApiClientError } from 'api/client';
import { shouldRetryTransientOnly } from 'app/modules/settings/modules/billing/utils/apiError';
import { EntitlementsResponse } from 'app/modules/settings/modules/billing/types/entitlements';
import { useNewBillingRoutesAvailable } from 'app/hooks/useNewBillingRoutesAvailable';

const BILLING_STALE_TIME = 5 * 60 * 1000;

/**
 * `GET /billing/entitlements`. Fetched whenever the NewBilling routes are
 * available — not only for accounts already known to be NewBilling-governed,
 * because this payload's `billingEngine` is what decides that (see
 * `useIsNewBillingEnabled`).
 */
export const useEntitlements = () => {
  const routesAvailable = useNewBillingRoutesAvailable();
  return useQuery<EntitlementsResponse, ApiClientError>(
    queryKey.billingEntitlements(),
    () => BillingApi.getEntitlements(),
    {
      staleTime: BILLING_STALE_TIME,
      enabled: routesAvailable,
      retry: shouldRetryEntitlements,
      // Once this has errored, a newly mounted observer must NOT re-fire it.
      // The query never holds data on that path, so `shouldLoadOnMount` would
      // otherwise fetch again for every one of the ~40 components that mount
      // `useSubscriptionInfo`. See `useSubscription` for the full loop.
      retryOnMount: false,
    },
  );
};

/**
 * Retry a transient failure (5xx, network: no HTTP status) twice before the
 * query errors. An error puts `useSubscriptionInfo` in its UNKNOWN state, which
 * falls back to the legacy account flags, so a one-off blip must not get there.
 * A 4xx (403 on an api that still role-gates the read, 404 routes off) is an
 * answer, not a blip, and is not retried.
 */
export const shouldRetryEntitlements = (
  failureCount: number,
  error: ApiClientError,
): boolean => shouldRetryTransientOnly(failureCount, error);
